/**
 * Regelversion 12: Aktivere Gegenparteien.
 *
 *  - Programme bewegen sich: Einmal im Jahr rücken die etablierten Parteien bei Themen, die nicht zu ihrem
 *    Markenkern gehören, ein Stück zur Mehrheitsmeinung (höchstens ±0,6 Stufen vom Ausgangsprogramm).
 *    Das verändert Koalitionsnähe und Abstimmungen im Bundestag.
 *  - Spitzenpersonal: Jede größere Partei hat eine Spitzenkandidatin oder einen Spitzenkandidaten.
 *    Affären, Rücktritte und neue Gesichter bewegen ihre Umfragewerte. Der Effekt verblasst mit der Zeit.
 *  - Die Computerregierung in der Opposition beschließt echte Gesetzesvorlagen mit deren Wirkungen
 *    (engine/opp11.ts, govLaw12): Problemdruck, Wirtschaft, Zustimmung und echtes Klagerisiko.
 * Die Änderungen leben im Spielstand (S.px.pos, S.px.mood, S.lead), damit ältere Partien unverändert bleiben.
 * Nur im Einzelspieler.
 */
import { S, log } from './state';
import { since } from './version';
import { rnd } from './rng';
import { clamp } from './util';
import { pub } from './opinion';
import { axes } from './axes';
import { PARTIES, BASE_PARTIES, OUTLETS } from '../content/parties';
import { AXN, WEIGHT } from '../content/axes';
import { press } from './media';
import { scoreRaw } from './rules';
import { vrOf } from './court';

export const rivOn = (): boolean => since(12) && !S.mp;
const A = () => S as any;

const FIRST = ['Anna', 'Jonas', 'Miriam', 'Tobias', 'Leonie', 'Markus', 'Sabine', 'Felix', 'Katrin', 'Daniel', 'Julia', 'Stefan', 'Nadine', 'Christoph', 'Elif', 'Hendrik', 'Paula', 'Ralf', 'Svenja', 'Malte'];
const LAST = ['Brandt', 'Kessler', 'Wagner', 'Lorenz', 'Hoffmann', 'Seidel', 'Krüger', 'Albers', 'Vogt', 'Neumann', 'Engel', 'Roth', 'Haas', 'Winkler', 'Böhm', 'Graf', 'Jansen', 'Kaya', 'Ritter', 'Busch'];
export type Lead = { n: string; m: number; pop: number };
export const leads = (): Record<string, Lead> => (A().lead ??= {});
export const leadOf = (k: string): Lead | null => A().lead?.[k] || null;
function newLead(k: string): Lead {
  const used = new Set(Object.values(leads()).map((l) => l.n));
  for (let i = 0; i < 20; i++) { const n = `${FIRST[Math.floor(rnd() * FIRST.length)]} ${LAST[Math.floor(rnd() * LAST.length)]}`; if (!used.has(n)) return { n, m: S.m, pop: 0 }; }
  return { n: `${FIRST[0]} ${LAST[0]}`, m: S.m, pop: 0 };
}

/** Jährlich (aus partyTick): Programme, Spitzenpersonal, verblassende Effekte. Zieht Zufall nur ab Version 12. */
export function rivTick(px: any) {
  if (!rivOn()) return;
  const pos = (px.pos ??= {}), mood = (px.mood ??= {});
  // Spitzenpersonal
  const big = BASE_PARTIES.filter((k: string) => PARTIES[k] && !PARTIES[k].gone && PARTIES[k].base >= 7);
  for (const k of big) if (!leads()[k]) leads()[k] = newLead(k);
  for (const k in mood) { mood[k] = Math.round(mood[k] * .5 * 100) / 100; if (Math.abs(mood[k]) < .2) delete mood[k]; }
  const k = big[Math.floor(rnd() * big.length)], r = rnd();
  if (k && r < .3) {
    const L = leads()[k], P = PARTIES[k];
    if (r < .12) { mood[k] = (mood[k] || 0) - 2; L.pop -= 1; log(`Affäre um ${L.n}, die Spitzenfigur von ${P.n}. Die Partei verliert in den Umfragen.`, 'good'); press(OUTLETS[3].n, `Affäre um ${L.n} (${P.a})`, 0); }
    else if (r < .2) { const old = L.n; leads()[k] = newLead(k); mood[k] = (mood[k] || 0) + 1.5; log(`${P.n} tauscht die Spitze aus: ${leads()[k].n} folgt auf ${old}. Die Partei schöpft neuen Mut.`, 'bad'); press(OUTLETS[0].n, `Neue Spitze bei ${P.a}: ${leads()[k].n}`, 0); }
    else { mood[k] = (mood[k] || 0) + 1; L.pop += 1; log(`${L.n} (${P.a}) punktet in einem großen Interview. Die Partei legt zu.`, 'bad'); }
  }
  // Programme rücken zur Mehrheit, nur außerhalb des Markenkerns
  let best: { k: string; a: string; d: number } | null = null;
  for (const p of BASE_PARTIES) {
    const P = PARTIES[p]; if (!P || P.gone) continue;
    const base = (px.base0 ??= {})[p] ??= { ...P.pos };
    const cur = (pos[p] ??= {});
    for (const a of axes()) {
      if ((P.w?.[a] ?? 1) >= 1.3) continue; // Markenkern bleibt
      const now = (base[a] ?? 0) + (cur[a] || 0), d = pub(a) - now;
      if (Math.abs(d) < .6) continue;
      const step = Math.sign(d) * .15, nd = clamp((cur[a] || 0) + step, -.6, .6);
      if (nd !== (cur[a] || 0)) { cur[a] = Math.round(nd * 100) / 100; if (!best || Math.abs(d) > Math.abs(best.d)) best = { k: p, a, d }; }
    }
  }
  if (best && rnd() < .5) log(`${PARTIES[best.k].n} rückt beim Thema ${AXN[best.a] || best.a} ein Stück auf die Mehrheit zu.`);
}

/** Opposition: Gesetz der Computerregierung mit echter Vorlage und echten Wirkungen. */
export function govLaw12(o: any, I: any, ls: any[]): { t: string; vr: number; d: string } | null {
  const T = ls[Math.floor(rnd() * ls.length)]; if (!T) return null;
  const P = PARTIES[o.chan], p: any = Object.fromEntries(T.ps.map((x: any) => [x.id, x.d]));
  for (const pa of T.ps) {
    const vals = pa.k === 'r' ? [pa.min, Math.round((pa.min + pa.max) / 2 / pa.st) * pa.st, pa.max] : pa.k === 'c' ? pa.o.map((_: string, i: number) => i) : [0, 1];
    let best = p[pa.id], bs = -1; for (const v of vals) { p[pa.id] = v; let s = 0; try { s = scoreRaw(P.pos, P.w, T.fx(p).pos); } catch { /* */ } if (s > bs) { bs = s; best = v; } } p[pa.id] = best;
  }
  let fx: any = {}; try { fx = T.fx(p) || {}; } catch { fx = {}; }
  const st = S.issues[I.id];
  st.sev = clamp(st.sev - clamp((fx.sev || 8) * .7, 4, 16), 0, 100); st.cut = Math.max(st.cut || 0, Math.min(.6, (fx.cut || .2) * .8));
  let w = 0; for (const g in fx.g || {}) w += (WEIGHT[g] || 0) * fx.g[g];
  o.appr = clamp(o.appr + clamp(w * .4, -2, 2), 10, 80);
  if (fx.gr) S.gr += fx.gr * .5;
  const win = Object.entries<number>(fx.g || {}).filter(([, v]) => v >= 1).map(([g]) => g), lose = Object.entries<number>(fx.g || {}).filter(([, v]) => v <= -1).map(([g]) => g);
  const GN: Record<string, string> = { rent: 'Rentner', fam: 'Familien', arb: 'Arbeitnehmer', unt: 'Unternehmen', jung: 'Junge' };
  const d = [win.length ? `hilft ${win.map((g) => GN[g]).join(', ')}` : '', lose.length ? `belastet ${lose.map((g) => GN[g]).join(', ')}` : ''].filter(Boolean).join(', ');
  return { t: T.t, vr: Math.round(clamp(vrOf(T, p), .03, .7) * 100) / 100, d };
}
